import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { signalrListen } from '@lib/signalr';
import type {
  ChatMessageDto,
  ChatTaskStatus,
  CreateChatMessageArgs,
  EditChatMessageArgs,
  UserInfoDto,
} from '@/ApiClient';

export type ChatTypeFilter = 'all' | 'messages' | 'my-tasks' | 'others-tasks' | 'agent';

// Chat messages + tasks the current user is involved in (as author or recipient).
// A plain per-user store — not global data — kept fresh via the `ChatUpdate` event.
export const useChat = defineStore('chat', () => {
  const api = useApi();

  const loaded = ref<ChatMessageDto[]>([]);
  const loading = ref(false);

  const meId = computed<string | undefined>(() => (useUser().current as UserInfoDto | null)?.id);

  const isFromMe = (message: ChatMessageDto): boolean => message.author?.id === meId.value;

  // Unread = a message addressed to me, by someone else, that I have not seen yet.
  const unreadCount = computed(
    () =>
      loaded.value.filter(
        (message) =>
          !isFromMe(message) &&
          message.recipients.some((recipient) => recipient.user.id === meId.value && !recipient.seen)
      ).length
  );

  const loadAll = async () => {
    loading.value = true;
    try {
      loaded.value = await api.chat_GetChatMessages(useUser().brandId);
    } finally {
      loading.value = false;
    }
  };

  const listen = () => {
    signalrListen('ChatUpdate', loadAll, { listenForever: true });
  };

  const create = async (args: CreateChatMessageArgs): Promise<ChatMessageDto> => {
    const message = await api.chat_CreateChatMessage(useUser().brandId, args);
    const index = loaded.value.findIndex((m) => m.id === message.id);
    if (index === -1) loaded.value.unshift(message);
    else loaded.value[index] = message;
    return message;
  };

  const setStatus = async (id: number, status: ChatTaskStatus): Promise<void> => {
    const message = await api.chat_UpdateStatus(useUser().brandId, id, { status });
    const index = loaded.value.findIndex((m) => m.id === id);
    if (index !== -1) loaded.value[index] = message;
  };

  const edit = async (id: number, args: EditChatMessageArgs): Promise<void> => {
    const message = await api.chat_Edit(useUser().brandId, id, args);
    const index = loaded.value.findIndex((m) => m.id === id);
    if (index !== -1) loaded.value[index] = message;
  };

  const markAllSeen = async (): Promise<void> => {
    if (unreadCount.value === 0) return;

    await api.chat_MarkAllSeen(useUser().brandId);
    loaded.value = loaded.value.map((message) => ({
      ...message,
      recipients: message.recipients.map((recipient) =>
        recipient.user.id === meId.value ? { ...recipient, seen: true } : recipient
      ),
    }));
  };

  return {
    loaded,
    loading,
    meId,
    unreadCount,
    loadAll,
    listen,
    create,
    edit,
    setStatus,
    markAllSeen,
  };
});
